import styles from './PasswordValidator.module.scss';
import { passwordCriteria } from '../../util/validation/passwordValidation';
import { FC, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Icon } from '@mui/material';
import GlobalContext from '../../contexts/GlobalContext';

export type TPasswordCriteriaItem = {
    label: string;
    valid: boolean;
};

export type TValidatorItemProps = {
    label: string;
    valid: boolean;
    testId?: string;
};

export type TPasswordValidatorProps = {
    password: string;
};

export const PasswordValidatorItem: FC<TValidatorItemProps> = (props) => {
    const { label, valid, testId } = props;
    let icon: ReactNode;
    if (valid) {
        icon = (
            <Icon color="success" data-testid="icon-pass">
                check_circle
            </Icon>
        );
    } else {
        icon = (
            <Icon color="disabled" data-testid="icon-fail">
                close
            </Icon>
        );
    }
    return (
        <Box className={styles.validatorItem} data-testid={testId}>
            <i data-testid="icon">{icon}</i>
            <p data-testid="label">{label}</p>
        </Box>
    );
};

const PasswordValidator: FC<TPasswordValidatorProps> = (props) => {
    const { password } = props;
    const { language } = useContext(GlobalContext);
    const [passwordLengthIsValid, setPasswordLengthIsValid] = useState(false);
    const [passwordUppercaseIsValid, setPasswordUppercaseIsValid] = useState(false);
    const [passwordLowercaseIsValid, setPasswordLowercaseIsValid] = useState(false);
    const [passwordSpecialIsValid, setPasswordSpecialIsValid] = useState(false);
    const allCriteriaMet = useMemo(() => {
        if (passwordLengthIsValid && passwordUppercaseIsValid && passwordLowercaseIsValid && passwordSpecialIsValid) {
            return true;
        } else {
            return false;
        }
    }, [passwordLengthIsValid, passwordUppercaseIsValid, passwordLowercaseIsValid, passwordSpecialIsValid]);

    useEffect(() => {
        password.match(passwordCriteria.length) !== null
            ? setPasswordLengthIsValid(true)
            : setPasswordLengthIsValid(false);
        password.match(passwordCriteria.uppercase) !== null
            ? setPasswordUppercaseIsValid(true)
            : setPasswordUppercaseIsValid(false);
        password.match(passwordCriteria.lowercase) !== null
            ? setPasswordLowercaseIsValid(true)
            : setPasswordLowercaseIsValid(false);
        password.match(passwordCriteria.special) !== null
            ? setPasswordSpecialIsValid(true)
            : setPasswordSpecialIsValid(false);
    }, [password]);

    return (
        <>
            {allCriteriaMet ? (
                <PasswordValidatorItem label={language.validation.password.all} valid={true} testId="password-valid" />
            ) : (
                <Box data-testid="password-validator">
                    <PasswordValidatorItem
                        label={language.validation.password.length}
                        valid={passwordLengthIsValid}
                        testId="password-length"
                    />
                    <PasswordValidatorItem
                        label={language.validation.password.uppercase}
                        valid={passwordUppercaseIsValid}
                        testId="password-uppercase"
                    />
                    <PasswordValidatorItem
                        label={language.validation.password.lowercase}
                        valid={passwordLowercaseIsValid}
                        testId="password-lowercase"
                    />
                    <PasswordValidatorItem
                        label={language.validation.password.special}
                        valid={passwordSpecialIsValid}
                        testId="password-special"
                    />
                </Box>
            )}
        </>
    );
};

export default PasswordValidator;
